import { Color } from 'three';
import { computed, defineComponent, inject, onMounted, provide, ref, watch } from 'vue';

import { gsap } from '@resn/gsap';

import { themeParams } from '~/components/gl/Foreground/data';
import { DEFAULT_PALETTE_ID, getPaletteThemeFromId, PALETTE_THEME } from '~/core/constants';

const ColorPaletteKey = 'ColorPalette';

/** @deprecated Legacy sandbox index → `PALETTE_THEME` key for `setColorPaletteIndex` only. */
const PALETTE_LEGACY_PALETTE_IDS = [
    'default',
    'base',
    'chase',
    'grail',
    'goldenTicket',
    'mega',
    'super',
    'infinity',
];

export const useColorPalette = () => {
    const env = inject(ColorPaletteKey);
    if (!env) {
        throw new Error('useColorPalette must be used within a ColorPaletteProvider');
    }
    return env;
};

const createColors = () => [new Color(), new Color(), new Color()];

const ColorPaletteProvider = defineComponent({
    setup() {
        const tweens = { pr: 0 };

        const colorsFrom = createColors();
        const colorsTo = createColors();
        const finalColors = createColors();

        const colorPaletteId = ref(DEFAULT_PALETTE_ID);
        /** When set, overrides `PALETTE_THEME` / `colorPaletteId` until cleared. */
        const customPaletteHex = ref(null);

        const paletteHexTriplet = computed(() => {
            if (customPaletteHex.value?.length === 3) return customPaletteHex.value;
            return getPaletteThemeFromId(colorPaletteId.value);
        });

        const applyThemeRow = (row) => {
            if (!row) return;
            customPaletteHex.value = null;
            colorPaletteId.value = row.paletteId;
        };

        /**
         * @param {unknown} tier — `themeParams[].id`, or `null` to reset when no `paletteId` override
         * @param {boolean | { paletteId?: string, isGold?: boolean }} [second] — `true` = golden ticket; or `{ paletteId }` for pack / wax colors
         */
        const setColorPalette = (paletteId, second = false) => {
            const opts =
                typeof second === 'object' && second !== null && !Array.isArray(second)
                    ? second
                    : { isGold: Boolean(second) };

            if (opts.isGold) {
                applyThemeRow(themeParams.find((_) => _.id == 'golden-ticket'));
                return;
            }
            if (opts.paletteId != null) {
                customPaletteHex.value = null;
                colorPaletteId.value = PALETTE_THEME[opts.paletteId]
                    ? opts.paletteId
                    : DEFAULT_PALETTE_ID;
                return;
            }
            colorPaletteId.value = paletteId || DEFAULT_PALETTE_ID;

            applyThemeRow(themeParams.find((_) => _.id == colorPaletteId.value));
        };

        /** @param {[string, string, string] | null} colors — hex strings, or `null` to clear override */
        const setCustomColorPalette = (colors) => {
            if (!colors || colors.length !== 3) {
                customPaletteHex.value = null;
                return;
            }
            customPaletteHex.value = colors;
        };

        /** @deprecated Prefer `setColorPalette(null, { paletteId })` or tier-based `setColorPalette`. */
        const setColorPaletteIndex = (i) => {
            const id = PALETTE_LEGACY_PALETTE_IDS[i];
            if (id) setColorPalette(null, { paletteId: id });
            else setColorPalette(null);
        };

        const onColorPalette = () => {
            const colors = paletteHexTriplet.value;

            colors.forEach((color, i) => {
                colorsFrom[i].set(finalColors[i]);
                colorsTo[i].set(color);
            });

            gsap.killTweensOf(tweens);
            gsap.fromTo(
                tweens,
                { pr: 0 },
                {
                    pr: 1,
                    duration: 1,
                    ease: 'sine.out',
                    onUpdate: () => {
                        return finalColors.forEach((color, i) =>
                            color.lerpColors(colorsFrom[i], colorsTo[i], tweens.pr)
                        );
                    },
                }
            );
        };

        onMounted(onColorPalette);

        watch(paletteHexTriplet, onColorPalette);

        provide(ColorPaletteKey, {
            colorPalette: finalColors,
            colorPaletteId,
            customPaletteHex,
            setColorPalette,
            setCustomColorPalette,
            setColorPaletteIndex,
        });
    },

    render() {
        if (this.$slots.default) {
            return this.$slots.default();
        }

        return null;
    },
});

export default ColorPaletteProvider;
